// deps
import React, { memo, useCallback, useEffect } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// helpers
import { getOverlayHeaderBannerAssets } from "./helper";

// styles
import {
    AnimatedInner,
    BackgroundLayer,
    BodyCopyBlock,
    DashedDivider,
    DashedDividerWrapper,
    Overlay,
    Root,
    SavingsTextShimmerWrap,
    StyledBackgroundImage,
    StyledSavingsText,
} from "./styles";

// types
import { OverlayHeaderBanner } from "@swiggy-private/cart-types";

// test-ids
import { TOING_HEADER_BANNER_TEST_IDS } from "@swiggy-private/cart-test-ids";

export interface ToingHeaderBannerProps {
    overlayHeaderBanner: OverlayHeaderBanner;
    savingsCallout: string;
    shouldAnimate: boolean;
    setHasSeenAnimation: (hasSeenAnimation: boolean) => void;
    onImpression?: VoidFunction;
    onBannerClick?: VoidFunction;
}

const ToingHeaderBanner = ({
    overlayHeaderBanner,
    savingsCallout,
    shouldAnimate,
    setHasSeenAnimation,
    onImpression,
    onBannerClick,
}: ToingHeaderBannerProps) => {
    const { title, subtitle, assets } = overlayHeaderBanner;

    useEffect(() => {
        onImpression?.();
    }, [onImpression]);
    const { backgroundBannerAssetId } = getOverlayHeaderBannerAssets(assets);

    const handleAnimationEnd = useCallback(
        (event: React.AnimationEvent<HTMLDivElement>) => {
            if (event.target !== event.currentTarget) {
                return;
            }
            setHasSeenAnimation(true);
        },
        [setHasSeenAnimation],
    );

    return (
        <Root $animate={shouldAnimate} data-testid={TOING_HEADER_BANNER_TEST_IDS.ROOT}>
            {!isEmpty(backgroundBannerAssetId) && (
                <BackgroundLayer>
                    <StyledBackgroundImage cloudinaryId={backgroundBannerAssetId} width="100%" height="150px" />
                </BackgroundLayer>
            )}
            <AnimatedInner $animate={shouldAnimate} onAnimationEnd={shouldAnimate ? handleAnimationEnd : undefined}>
                <Overlay $hasSavingsText={isEmpty(savingsCallout)} onClick={onBannerClick}>
                    <SavingsTextShimmerWrap>
                        <StyledSavingsText
                            variant="V2.caption.neutral.medium"
                            color="text_inverse_Med_Emphasis"
                            $shimmer={shouldAnimate}
                            data-testid={TOING_HEADER_BANNER_TEST_IDS.SAVINGS_CALLOUT}
                        >
                            {savingsCallout}
                        </StyledSavingsText>
                    </SavingsTextShimmerWrap>

                    <DashedDividerWrapper>
                        <DashedDivider />
                    </DashedDividerWrapper>
                    <BodyCopyBlock $hasSavingsText={isEmpty(savingsCallout)}>
                        <Text
                            variant="V2.body1.neutral.bold"
                            color="text_inverse_Highest_Emphasis"
                            data-testid={TOING_HEADER_BANNER_TEST_IDS.TITLE}
                        >
                            {title}
                        </Text>
                        <Text
                            variant="V2.body1.neutral.bold"
                            color="text_inverse_Highest_Emphasis"
                            data-testid={TOING_HEADER_BANNER_TEST_IDS.SUBTITLE}
                        >
                            {subtitle}
                        </Text>
                    </BodyCopyBlock>
                </Overlay>
            </AnimatedInner>
        </Root>
    );
};

export default memo(ToingHeaderBanner);
